/**
 * Shared domain types. Used by the server (API + services) and by the web client.
 * Dates are ISO strings: 'YYYY-MM-DD' for calendar dates, full ISO 8601 for timestamps.
 */

/**
 * Roles (the same four groups as the guide): employee "Mitarbeitende", external "Externe", office "Büro und
 * Geschäftsführung" (office, HR and management are one role: the assistant of the management works with the same data
 * as the owner), admin "Technischer Admin". Leading a project is not a role: it is given per project (Projekt > Team).
 * Job titles ("Projektleiter", "Abteilungsleiter") are free text on the person and grant nothing.
 */
export const ROLES = ['employee', 'external', 'office', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const PERSON_KINDS = ['employee', 'external'] as const;
export type PersonKind = (typeof PERSON_KINDS)[number];

export const LANGUAGES = ['de', 'en', 'tr'] as const;
export type Language = (typeof LANGUAGES)[number];

/** How an entry is treated in billing. */
export const BILLING_TYPES = ['billable', 'included', 'non_billable'] as const;
export type BillingType = (typeof BILLING_TYPES)[number];

export const ENTRY_STATUSES = ['draft', 'submitted', 'approved_l1', 'approved', 'rejected', 'billed'] as const;
export type EntryStatus = (typeof ENTRY_STATUSES)[number];

export const PROJECT_BILLING_MODES = ['hourly', 'fixed_fee', 'mixed', 'internal'] as const;
/** Colour of a project: one of the eight hues of the validated categorical palette, in their fixed order (round 4). */
export const PROJECT_COLORS = ['blue', 'orange', 'aqua', 'yellow', 'magenta', 'green', 'violet', 'red'] as const;
export type ProjectColor = (typeof PROJECT_COLORS)[number];
/**
 * Approval of the hours of a project (round 4, request 29): office only (one stage), project lead only (one stage),
 * project lead then office (two stages), or the person's own approval with the submission.
 */
export const APPROVAL_MODES = ['office', 'lead', 'lead_office', 'self'] as const;
export type ApprovalMode = (typeof APPROVAL_MODES)[number];
export type ProjectBillingMode = (typeof PROJECT_BILLING_MODES)[number];

export const PROJECT_FORMA_STATES = ['not_linked', 'active', 'archived', 'deleted', 'access_lost'] as const;
export type ProjectFormaState = (typeof PROJECT_FORMA_STATES)[number];

export const TASK_SOURCES = ['forma', 'manual'] as const;
export type TaskSource = (typeof TASK_SOURCES)[number];

export const TASK_FORMA_STATES = ['active', 'deleted', 'access_lost'] as const;
export type TaskFormaState = (typeof TASK_FORMA_STATES)[number];

export const STATUS_GROUPS = ['active', 'done', 'closed'] as const;
export type StatusGroup = (typeof STATUS_GROUPS)[number];

/** Key of a task category (table task_category, maintained in Verwaltung > Kategorien). */
export type ManualCategory = string;

export const MEMBER_ROLES = ['lead', 'member'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const TASK_SCOPES = ['all', 'selected'] as const;
export type TaskScope = (typeof TASK_SCOPES)[number];

export const DOC_KINDS = ['stundennachweis', 'abschlag'] as const;
export type DocKind = (typeof DOC_KINDS)[number];

export const DOC_STATUSES = ['draft', 'final', 'invoiced', 'paid', 'cancelled'] as const;
export type DocStatus = (typeof DOC_STATUSES)[number];

/**
 * Kinds of absence. Rules per kind (approval, leave entitlement, self service, health data) are in leave.ts.
 * vacation Urlaub, special Sonderurlaub, training Weiterbildung/Bildungsurlaub, overtime Freizeitausgleich,
 * unpaid unbezahlter Urlaub, sick Krankheit, child_sick Kind krank, parental Mutterschutz/Elternzeit, other.
 */
export const ABSENCE_KINDS = ['vacation', 'special', 'training', 'overtime', 'unpaid', 'sick', 'child_sick', 'parental', 'other'] as const;
export type AbsenceKind = (typeof ABSENCE_KINDS)[number];

export const ABSENCE_STATUSES = ['requested', 'approved', 'rejected', 'cancelled'] as const;
export type AbsenceStatus = (typeof ABSENCE_STATUSES)[number];

export const FORMA_AUTH_MODES = ['mock', 'user_3lo', 'ssa'] as const;
export type FormaAuthMode = (typeof FORMA_AUTH_MODES)[number];

/** Forma issue status values (API enum). */
export const FORMA_STATUSES = [
  'draft',
  'open',
  'pending',
  'in_progress',
  'in_review',
  'completed',
  'not_approved',
  'in_dispute',
  'closed',
] as const;
export type FormaStatus = (typeof FORMA_STATUSES)[number];

export function statusGroupOf(status: string): StatusGroup {
  if (status === 'completed') return 'done';
  if (status === 'closed') return 'closed';
  return 'active';
}

/** Minimal user context used for every permission decision. */
export interface Actor {
  personId: string;
  displayName: string;
  role: Role;
  kind: PersonKind;
  language: Language;
  permissions: ReadonlySet<Permission>;
  /** Projects where the actor is lead (currently valid membership). */
  leadProjectIds: ReadonlySet<string>;
  /** Projects where the actor is a member (any role, currently valid). */
  memberProjectIds: ReadonlySet<string>;
  /**
   * Rights the account holds, before company switches take features away for everybody (e.g. team figures off).
   * Only for granting rights and changing accounts (who may hand on what); every reading uses `permissions`.
   */
  grantable?: ReadonlySet<Permission>;
}

export const PERMISSIONS = [
  'time.approve.final',
  'time.manage',
  'period.manage',
  'billing.manage',
  'rates.sales.view',
  'rates.cost.view',
  'hr.manage',
  'kpi.team.view',
  'workload.manage',
  'projects.manage',
  'users.manage',
  'settings.manage',
  'backup.manage',
  'audit.view',
  'data.export.all',
  'sync.manage',
  'analytics.view',
  'customers.manage',
  'announcements.manage',
  'rights.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export interface AppSettings {
  /**
   * name: company name shown in the app, on the sign-in page and in exports.
   * standardsUrl: page where the company keeps its standards and layouts (SharePoint); the start page only links to it.
   */
  company: {
    appName: string;
    name: string;
    defaultHolidayCalendar: string;
    place: string;
    standardsUrl: string;
    /**
     * Quick links in the side bar above the own name (request 15 of round 4): company standards and templates, the notes
     * of the weekly coordination meeting (SharePoint) and the company website. A link appears only when its address is
     * set; an empty label means the standard label of the language.
     */
    meetingNotesUrl: string;
    websiteUrl: string;
    linkLabels: { standards: string; meetings: string; website: string };
  };
  /**
   * Approval of the hours, the company default (each project can choose its own, round 4). `mode` decides; `stages` is
   * the older form (2 = project lead, then office; 1 = project lead) and is read where no mode is stored.
   */
  approval: { stages: 1 | 2; allowSelfApproval: boolean; mode?: ApprovalMode };
  time: {
    stepMinutes: number;
    requireDescriptionFor: BillingType[];
    warnDailyMinutes: number;
    maxFutureDays: number;
    /** Days after the end of a week (Sunday) by which the week should be submitted. */
    submitDeadlineDays: number;
    /** First day recorded in Projektzeit (go-live). Target/actual figures and the capacity cache start here. */
    trackingStart: string | null;
    /** Choices for "Arbeitsort" in the booking dialog. */
    workLocations: string[];
    /**
     * The person chooses the billing type (abrechenbar, im Pauschalhonorar, nicht abrechnen) when booking;
     * the rule of task and project is the default, a fixed rule (category or task) stays fixed. Approvers can
     * change it on other people's entries. Hours to invoice stay with billing staff.
     */
    ownBillingType: boolean;
    /**
     * Hours that are recorded but do not count as working time for the extra hours (e.g. voluntary training in the
     * evening, a business development event at the weekend): marked per entry, preset by the category. They stay in
     * the project figures and the evaluation, but never change the overtime balance. Off: no such distinction.
     */
    noOvertime: boolean;
  };
  billing: {
    numberPrefix: Record<DocKind, string>;
    dayTextPrefix: 'discipline' | 'name' | 'code' | 'none';
    /** task in the day text of a Stundennachweis: only for entries without a description, or always as [Task] */
    dayTextTask: 'fallback' | 'always';
    /** work locations named in brackets in the day text (e.g. Baustelle); office and home office usually not */
    dayTextLocations: string[];
    defaultLanguage: 'de' | 'en';
    vatPercent: number;
    issuerName: string;
    issuerAddress: string;
  };
  forma: {
    deepLinkTemplate: string;
    /** Minutes between incremental runs (5, 10, 15, 30 or 60). */
    syncIntervalMinutes: number;
    /** 'daily': every day 06:00-23:59 local time; 'workhours': Monday to Saturday 06:00-20:59; 'always': around the clock. */
    syncWindow: 'daily' | 'workhours' | 'always';
    /** New projects of the Forma hub are taken over at night (booking only for the project team). */
    autoImportProjects: boolean;
  };
  security: {
    requireTotpForExternal: boolean;
    requireTotpForPrivileged: boolean;
    sessionIdleHours: number;
    sessionMaxDays: number;
    /**
     * Additional confirmation for Abrechnung and Kunden (request 31 of round 4): with it switched on, these modules
     * open only after the person confirmed again (password, or the authenticator code when one is set up) within the
     * last `minutes`. Changed only by holders of the module rights (not by the technical administration).
     */
    stepUp: { billing: boolean; customers: boolean; minutes: number };
  };
  bi: { pseudonymize: boolean };
  /**
   * teamVisibleKinds: kinds shown by name in the team calendar; all others (always sickness) as "abwesend".
   * documentFromDay: a document (e.g. sick note) is expected from this calendar day of the absence on (0 = never).
   * attachmentRetentionMonths: documents are removed this many months after the end of the absence (0 = kept).
   */
  /**
   * saturdayLeaveTR: for people on a Turkish holiday calendar, Saturdays inside vacation count as leave days (5-day
   * week whose contract does not make Saturday a weekly rest day, see docs/LEAVE.md); off: only contract working days.
   */
  leave: { teamVisibleKinds: string[]; documentFromDay: { sick: number; child_sick: number }; attachmentRetentionMonths: number; saturdayLeaveTR: boolean };
  /**
   * Approved absences as "Abwesend" appointments in Outlook (Microsoft Graph): in the calendar of each person
   * (free/busy for meeting planning in Outlook and Teams) and optionally in a shared team calendar.
   */
  outlook: { enabled: boolean; personalCalendars: boolean; teamMailbox: string };
  /**
   * teamEnabled: personal performance figures of the team (can stay off until e.g. a works agreement exists).
   * weekToleranceMinutes: a finished week counts as under- or over-booked beyond this difference to capacity.
   * leadLoadLevels: project leads and planners see load levels (colours, no hours) in the workload views.
   * Someone who changes planned hours on purpose can roughly derive a person's available hours from the
   * colour change; switch it off when that is not wanted (HR and management keep the hours).
   */
  kpi: { teamEnabled: boolean; weekToleranceMinutes: number; leadLoadLevels: boolean };
  /** defaultPaymentDays: payment term (days) proposed when the commercial terms of a project are created. */
  customers: { defaultPaymentDays: number };
  /**
   * Built-in automations (run by the platform itself, no external service, no extra licence). Every rule only
   * produces notifications for people who are allowed to see the underlying data anyway.
   * weeklyReminder: the person gets a reminder when the week is not submitted on the deadline day.
   * monthCloseReminder: office/management see a reminder in the first working days when the last month is open.
   * missingEntries: the person sees days without booking (working days, no absence) of the last days.
   * pendingApprovals: approvers see how many items wait (and for how long).
   */
  automations: {
    weeklyReminder: boolean;
    monthCloseReminder: boolean;
    missingEntries: boolean;
    pendingApprovals: boolean;
    /** working days in the past that are checked for missing entries */
    missingEntriesLookbackDays: number;
    /** pending approvals older than this many days are highlighted */
    pendingEscalationDays: number;
  };
}

export const DEFAULT_SETTINGS: AppSettings = {
  company: {
    appName: 'Projektzeit',
    name: '',
    defaultHolidayCalendar: 'DE-BE',
    place: '',
    standardsUrl: '',
    meetingNotesUrl: '',
    websiteUrl: '',
    linkLabels: { standards: '', meetings: '', website: '' },
  },
  approval: { stages: 1, allowSelfApproval: false, mode: 'office' },
  time: {
    stepMinutes: 15,
    requireDescriptionFor: ['billable', 'included'],
    warnDailyMinutes: 600,
    maxFutureDays: 14,
    submitDeadlineDays: 1,
    trackingStart: null,
    workLocations: ['Büro', 'Homeoffice', 'Baustelle', 'Beim Auftraggeber', 'Dienstreise'],
    ownBillingType: true,
    noOvertime: false,
  },
  billing: {
    numberPrefix: { stundennachweis: 'SN', abschlag: 'AB' },
    dayTextPrefix: 'code',
    dayTextTask: 'fallback',
    dayTextLocations: ['Baustelle', 'Beim Auftraggeber', 'Dienstreise'],
    defaultLanguage: 'de',
    vatPercent: 19,
    issuerName: '',
    issuerAddress: '',
  },
  forma: {
    deepLinkTemplate: 'https://acc.autodesk.com/docs/issues/projects/{projectId}/issues?issueId={issueId}',
    syncIntervalMinutes: 15,
    syncWindow: 'daily',
    autoImportProjects: false,
  },
  security: { requireTotpForExternal: true, requireTotpForPrivileged: true, sessionIdleHours: 12, sessionMaxDays: 14, stepUp: { billing: false, customers: false, minutes: 15 } },
  bi: { pseudonymize: false },
  leave: { teamVisibleKinds: ['vacation', 'training'], documentFromDay: { sick: 4, child_sick: 1 }, attachmentRetentionMonths: 0, saturdayLeaveTR: false },
  outlook: { enabled: false, personalCalendars: true, teamMailbox: '' },
  kpi: { teamEnabled: true, weekToleranceMinutes: 120, leadLoadLevels: true },
  customers: { defaultPaymentDays: 30 },
  automations: {
    weeklyReminder: true,
    monthCloseReminder: true,
    missingEntries: true,
    pendingApprovals: true,
    missingEntriesLookbackDays: 7,
    pendingEscalationDays: 3,
  },
};

/** Standard HOAI 2021 §55 (1) weights for Technische Ausrüstung, in percent. Editable per project. */
export const HOAI_TGA_PHASES: { key: string; label: string; weight: number }[] = [
  { key: 'LPH1', label: 'LPH 1 Grundlagenermittlung', weight: 2 },
  { key: 'LPH2', label: 'LPH 2 Vorplanung', weight: 9 },
  { key: 'LPH3', label: 'LPH 3 Entwurfsplanung', weight: 17 },
  { key: 'LPH4', label: 'LPH 4 Genehmigungsplanung', weight: 2 },
  { key: 'LPH5', label: 'LPH 5 Ausführungsplanung', weight: 22 },
  { key: 'LPH6', label: 'LPH 6 Vorbereitung der Vergabe', weight: 7 },
  { key: 'LPH7', label: 'LPH 7 Mitwirkung bei der Vergabe', weight: 5 },
  { key: 'LPH8', label: 'LPH 8 Objektüberwachung', weight: 35 },
  { key: 'LPH9', label: 'LPH 9 Objektbetreuung', weight: 1 },
];

/** Holiday calendars supported out of the box. */
export const HOLIDAY_CALENDARS = [
  'DE-BW', 'DE-BY', 'DE-BE', 'DE-BB', 'DE-HB', 'DE-HH', 'DE-HE', 'DE-MV',
  'DE-NI', 'DE-NW', 'DE-RP', 'DE-SL', 'DE-SN', 'DE-ST', 'DE-SH', 'DE-TH', 'TR',
] as const;
export type HolidayCalendar = (typeof HOLIDAY_CALENDARS)[number];
